/**
 * Base API functions for making HTTP requests
 */
import type { ErrorResponse } from "./types";

export const API_BASE_URL =
    process.env.VITE_API_URL || "http://localhost:8080";

/**
 * Creates a fetch request with default headers
 * Enhanced version with error handling
 */
export async function apiRequest<T>(
    endpoint: string,
    options?: RequestInit
): Promise<T> {
    const url = endpoint.startsWith("http")
        ? endpoint
        : `${API_BASE_URL}${endpoint}`;

    const defaultHeaders: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (options?.body instanceof FormData) {
        delete defaultHeaders["Content-Type"];
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options?.headers,
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.statusText}`;

        try {
            const errorJson = JSON.parse(errorText) as ErrorResponse;
            errorMessage = errorJson.message || errorMessage;
        } catch {
            // If not JSON, use the text or default message
            if (errorText) {
                errorMessage = errorText;
            }
        }

        // Handle 401 by throwing a specific error that can be caught by auth handler
        if (response.status === 401) {
            // Check for specific session expired message
            if (errorMessage === "Session expired") {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("session-expired"));
                }
            }
            const error = new Error(errorMessage) as Error & { status: number };
            error.status = 401;
            throw error;
        }

        throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
        return undefined as T;
    }

    return response.json();
}

/**
 * Creates an authenticated API request with JWT token
 */
export async function authenticatedRequest<T>(
    endpoint: string,
    token: string,
    options?: RequestInit
): Promise<T> {
    return apiRequest<T>(endpoint, {
        ...options,
        headers: {
            Authorization: `Bearer ${token}`,
            ...options?.headers,
        },
    });
}
